/**
 * Normalize a hashtag for API request bodies (no leading `#`).
 */
export function stripHashtagPrefixForApi(tag: string | unknown): string {
  return String(tag ?? '')
    .replace(/^#+/, '')
    .trim();
}

/**
 * v2 observation list / export body: omit `hashtags` when not filtering (empty `[]` can match nothing).
 * When present, each tag is sent without a leading `#`. Returns a shallow copy; does not mutate `body`.
 */
export function normalizeObservationFilterHashtagsForV2(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...body };
  const h = out['hashtags'];
  if (h === undefined || h === null) {
    return out;
  }
  if (!Array.isArray(h) || h.length === 0) {
    delete out['hashtags'];
    return out;
  }
  const stripped = h.map((tag) => stripHashtagPrefixForApi(tag)).filter((s) => s.length > 0);
  if (stripped.length === 0) {
    delete out['hashtags'];
  } else {
    out['hashtags'] = stripped;
  }
  return out;
}

/**
 * Appends observation hashtags to a multipart `create_observation` body.
 * Mutates `formData` in place and returns the same instance for chaining.
 * Values are appended without a leading `#`.
 */
export function appendObservationHashtagsToFormData(
  formData: FormData,
  hashtags: string[] | null | undefined,
): FormData {
  for (const tag of hashtags || []) {
    const s = stripHashtagPrefixForApi(tag);
    if (s) {
      formData.append('hashtags[]', s);
    }
  }
  return formData;
}

/**
 * Rebuild `FormData` so each `hashtags[]` field is sent without a leading `#`.
 * Use for `update_observation` multipart bodies.
 */
export function formDataWithObservationHashtagsStrippedForApi(formData: FormData): FormData {
  const out = new FormData();
  formData.forEach((value, key) => {
    if (key === 'hashtags[]' && typeof value === 'string') {
      const s = stripHashtagPrefixForApi(value);
      if (s) {
        out.append(key, s);
      }
    } else {
      out.append(key, value);
    }
  });
  return out;
}
